import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { PayPalButtons } from '@paypal/react-paypal-js';
import { Check, Shield, Star, Users, Crown, Building, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tierIcons = { supporter: Star, community_member: Users, advocate: Shield, founding_member: Crown, organizational_partner: Building };
const tierColors = { supporter: 'border-sky-custom', community_member: 'border-forest', advocate: 'border-gold', founding_member: 'border-navy', organizational_partner: 'border-navy' };

export default function Membership() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [selectedTier, setSelectedTier] = useState(null);

  useEffect(() => {
    axios.get(`${API}/membership/tiers`).then(r => setTiers(r.data)).catch(() => {
      setTiers([
        { id: 'supporter', name: 'Supporter', price: 10, description: 'Support the Legal Defense Fund', features: ['Legal Defense Fund Contribution', 'Monthly Newsletter', 'Community Updates', 'Safe Florida Sticker'], popular: false },
        { id: 'community_member', name: 'Community Member', price: 25, description: 'Active community participation', features: ['All Supporter Benefits', 'Member Portal Access', 'Community Forum', 'Event Invitations', 'Quarterly Reports'], popular: false },
        { id: 'advocate', name: 'Advocate', price: 99, description: 'Amplify your voice for Florida', features: ['All Community Benefits', 'Priority Zone Updates', 'Seat at the Table Access', 'Direct Leadership Contact', 'Exclusive Briefings', 'Voting Rights'], popular: true },
        { id: 'founding_member', name: 'Founding Member', price: 250, description: 'Full membership with maximum impact', features: ['All Advocate Benefits', 'Founding Member Recognition', 'Strategy Summit', 'One-on-One with Chuck Clark', 'Full Document Library', 'Named in Annual Report'], popular: false },
        { id: 'organizational_partner', name: 'Organizational Partner', price: 500, description: 'For businesses and organizations', features: ['All Founding Benefits', 'Logo on Website', 'Co-branded Events', 'Priority Business Advocacy', 'Custom Impact Reports', 'Multi-seat Access (5)'], popular: false },
      ]);
    });
  }, []);

  const handlePaymentSuccess = async (details, tier) => {
    try {
      await axios.post(`${API}/membership/subscribe`, {
        paypal_order_id: details.id,
        amount: tier.price,
        tier: tier.id
      }, { withCredentials: true });
      toast.success(`${tier.name} membership activated! Welcome to Safe Florida.`);
      navigate('/dashboard');
    } catch {
      toast.error('Payment recorded but activation failed. Contact support.');
    }
  };

  return (
    <div className="pt-16" data-testid="membership-page">
      {/* Header */}
      <section className="bg-navy py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-gold font-body mb-3">Membership Tiers</p>
          <h1 className="text-4xl sm:text-5xl font-heading font-bold text-white mb-4">Choose Your Level of Impact</h1>
          <p className="text-lg text-white/80 font-body max-w-2xl mx-auto">Every tier strengthens the movement. Choose the membership that fits your commitment to protecting Florida.</p>
        </div>
      </section>

      {/* Tiers Grid */}
      <section className="py-20 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            {tiers.map(tier => {
              const Icon = tierIcons[tier.id] || Star;
              const borderColor = tierColors[tier.id] || 'border-gray-200';
              return (
                <div key={tier.id} className={`relative bg-white border-2 ${tier.popular ? 'border-gold shadow-lg scale-[1.02]' : borderColor} rounded-lg overflow-hidden card-hover flex flex-col`} data-testid={`tier-card-${tier.id}`}>
                  {tier.popular && (
                    <div className="bg-gold text-navy text-xs font-body font-bold text-center py-1 tracking-wide">MOST POPULAR</div>
                  )}
                  <div className="p-5 flex-1 flex flex-col">
                    <Icon className={`w-8 h-8 mb-3 ${tier.popular ? 'text-gold' : 'text-navy'}`} />
                    <h3 className="font-heading font-bold text-navy text-lg">{tier.name}</h3>
                    <p className="text-xs text-gray-600 font-body mb-3">{tier.description}</p>
                    <div className="mb-4">
                      <span className="text-3xl font-heading font-bold text-navy">${tier.price}</span>
                      <span className="text-sm text-gray-500 font-body">/mo</span>
                    </div>
                    <ul className="space-y-2 mb-5 flex-1">
                      {tier.features?.map(f => (
                        <li key={f} className="flex items-start gap-2 text-xs font-body text-gray-700">
                          <Check className="w-3 h-3 text-forest flex-shrink-0 mt-0.5" /> {f}
                        </li>
                      ))}
                    </ul>

                    {user ? (
                      user.membership_status === 'active' ? (
                        <div className="text-center p-2 bg-forest/10 rounded">
                          <p className="text-xs font-body font-bold text-forest">Active Member</p>
                          <Link to="/dashboard" className="text-xs text-navy underline font-body">Dashboard</Link>
                        </div>
                      ) : selectedTier === tier.id ? (
                        <div data-testid={`paypal-tier-${tier.id}`}>
                          <PayPalButtons
                            style={{ layout: 'vertical', shape: 'rect', height: 35 }}
                            createOrder={(data, actions) => actions.order.create({
                              purchase_units: [{ amount: { value: tier.price.toFixed(2) }, description: `Safe Florida ${tier.name} Membership` }]
                            })}
                            onApprove={(data, actions) => actions.order.capture().then(d => handlePaymentSuccess(d, tier))}
                            onError={() => toast.error('Payment failed.')}
                          />
                          <button onClick={() => setSelectedTier(null)} className="text-xs text-gray-500 font-body mt-1 w-full text-center">Cancel</button>
                        </div>
                      ) : (
                        <Button onClick={() => setSelectedTier(tier.id)} className={`w-full font-body text-xs ${tier.popular ? 'bg-gold text-navy hover:bg-gold/90' : 'btn-primary-safe'}`} data-testid={`select-tier-${tier.id}`}>
                          Select {tier.name}
                        </Button>
                      )
                    ) : (
                      <Link to="/login" data-testid={`tier-login-${tier.id}`}>
                        <Button className="w-full btn-primary-safe font-body text-xs">Log In to Subscribe</Button>
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-gold font-body mb-3">All Members Share</p>
            <h2 className="text-3xl font-heading font-bold text-navy">One Movement. One Mission.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: 'Protect Families', desc: 'Your membership directly funds programs that safeguard Florida families from threats to their communities and way of life.', icon: Shield },
              { title: 'Secure Land', desc: 'Contribute to strategic land preservation efforts protecting wildlife habitats, waterways, and community green spaces.', icon: Star },
              { title: 'Strengthen Communities', desc: 'Join a unified voice of Floridians demanding accountability, transparency, and responsible development.', icon: Users },
            ].map(({ title, desc, icon: Icon }) => (
              <div key={title} className="text-center p-6">
                <div className="w-14 h-14 mx-auto mb-4 bg-navy/5 rounded-full flex items-center justify-center">
                  <Icon className="w-7 h-7 text-navy" />
                </div>
                <h3 className="font-heading font-bold text-navy text-lg mb-2">{title}</h3>
                <p className="text-sm text-gray-600 font-body">{desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <p className="font-signature text-gold text-3xl">Chuck Clark</p>
            <p className="text-xs text-gray-500 font-body mt-1">Founder & Leader, Safe Florida</p>
          </div>
        </div>
      </section>
    </div>
  );
}
